import { useState, useEffect } from 'react';
import api from '../api/axios';
import {
  MagnifyingGlassIcon,
  CloudIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  ServerStackIcon,
  CheckBadgeIcon,
  WifiIcon,
  DocumentTextIcon,
  FolderIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon as CheckBadgeSolid } from '@heroicons/react/24/solid';

type IconType = React.ComponentType<{ className?: string }>;

type StorageProvider = 's3' | 'sharepoint' | 'rustfs';

interface Company {
  id: string;
  company_name: string;
  company_code: string;
  storage_provider?: StorageProvider;
}

interface StorageConfig {
  is_configured?: boolean;
  storage_provider?: StorageProvider | null;
  // S3
  s3_access_key?: string;
  s3_secret_key?: string;
  s3_bucket_name?: string;
  s3_region?: string;
  s3_endpoint?: string;
  s3_root_prefix?: string;
  has_s3_access_key?: boolean;
  has_s3_secret_key?: boolean;
  // RustFS
  rustfs_endpoint?: string;
  rustfs_api_key?: string;
  rustfs_bucket_name?: string;
  rustfs_root_prefix?: string;
  has_rustfs_api_key?: boolean;
  // SharePoint
  sharepoint_client_id?: string;
  sharepoint_client_secret?: string;
  sharepoint_tenant_id?: string;
  sharepoint_site_id?: string;
  has_sharepoint_client_secret?: boolean;
}

interface CredentialFieldProps {
  label: string;
  field: string;
  isConfigured: boolean;
  value: string;
  placeholder: string;
  onChange: (field: string, value: string) => void;
  hint?: string;
}

function CredentialField({ label, field, isConfigured, value, placeholder, onChange, hint }: CredentialFieldProps) {
  const [revealed, setRevealed] = useState(false);
  const [editing, setEditing] = useState(false);

  const handleEdit = () => {
    setEditing(true);
    setRevealed(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        {isConfigured && !editing && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
            <CheckBadgeSolid className="w-3 h-3" /> Configured
          </span>
        )}
      </div>
      {isConfigured && !editing ? (
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
            <span className="text-sm text-slate-500 tracking-widest font-mono">
              {revealed ? '(hidden — click Change to update)' : '••••••••••••••••'}
            </span>
          </div>
          <button
            onClick={() => setRevealed(r => !r)}
            className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
            title={revealed ? 'Hide' : 'Show status'}
          >
            {revealed ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
          </button>
          <button
            onClick={handleEdit}
            className="px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
          >
            Change
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type={revealed ? 'text' : 'password'}
            value={value}
            onChange={(e) => onChange(field, e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
          />
          <button
            onClick={() => setRevealed(r => !r)}
            className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            {revealed ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
          </button>
          {isConfigured && (
            <button
              onClick={() => { setEditing(false); setRevealed(false); onChange(field, ''); }}
              className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              title="Cancel change"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

const PROVIDER_META: Record<StorageProvider, {
  Icon: IconType
  label: string
  desc: string
  color: string
  iconBg: string
  iconColor: string
  ring: string
}> = {
  s3:         { Icon: CloudIcon,        label: 'S3 Compatible', desc: 'AWS S3, MinIO, DO Spaces', color: 'text-blue-700 bg-blue-50 border-blue-200',       iconBg: 'bg-blue-100',   iconColor: 'text-blue-600',   ring: 'border-blue-400 bg-blue-50 ring-2 ring-blue-100' },
  sharepoint: { Icon: DocumentTextIcon, label: 'SharePoint',    desc: 'Microsoft integration',    color: 'text-indigo-700 bg-indigo-50 border-indigo-200', iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600', ring: 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-100' },
  rustfs:     { Icon: FolderIcon,       label: 'RustFS',        desc: 'Self-hosted file server',  color: 'text-violet-700 bg-violet-50 border-violet-200', iconBg: 'bg-violet-100', iconColor: 'text-violet-600', ring: 'border-violet-400 bg-violet-50 ring-2 ring-violet-100' },
};

export default function StorageConfiguration() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [config, setConfig] = useState<StorageConfig | null>(null);
  const [formValues, setFormValues] = useState<StorageConfig>({});
  const [provider, setProvider] = useState<StorageProvider>('s3');
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [testFile, setTestFile] = useState<File | null>(null);
  const [testWithUpload, setTestWithUpload] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/super-admin/companies');
      setCompanies(response.data.companies || []);
    } catch {
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = async (company: Company) => {
    setSelectedCompany(company);
    setConfig(null);
    setFormValues({});
    setIsEditing(false);
    setError('');
    setSuccess('');
    setDeleteConfirm(false);
    await fetchStorageConfig(company.id);
  };

  const fetchStorageConfig = async (companyId: string) => {
    setConfigLoading(true);
    try {
      const response = await api.get(`/super-admin/companies/${companyId}/storage-config`);
      // Handle both flat and wrapped response shapes
      const data: StorageConfig = response.data?.config ?? response.data;
      setConfig(data);
      const p = (data.storage_provider as StorageProvider) || 's3';
      setProvider(p);
      setFormValues({
        storage_provider: p,
        s3_endpoint: data.s3_endpoint ?? '',
        s3_bucket_name: data.s3_bucket_name ?? '',
        s3_region: data.s3_region ?? '',
        s3_root_prefix: data.s3_root_prefix ?? '',
        s3_access_key: '',
        s3_secret_key: '',
        rustfs_endpoint: data.rustfs_endpoint ?? '',
        rustfs_bucket_name: data.rustfs_bucket_name ?? '',
        rustfs_root_prefix: data.rustfs_root_prefix ?? '',
        rustfs_api_key: '',
        sharepoint_client_id: data.sharepoint_client_id ?? '',
        sharepoint_client_secret: '',
        sharepoint_tenant_id: data.sharepoint_tenant_id ?? '',
        sharepoint_site_id: data.sharepoint_site_id ?? '',
      });
      // Auto-edit mode only for unconfigured tenants; configured ones get locked view
      setIsEditing(!data.is_configured);
    } catch {
      setError('Failed to load storage configuration');
      setConfig({ is_configured: false });
      setFormValues({ storage_provider: 's3' });
    } finally {
      setConfigLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  const handleProviderChange = (p: StorageProvider) => {
    setProvider(p);
    setFormValues(prev => ({ ...prev, storage_provider: p }));
  };

  const buildSavePayload = () => {
    const base = { storage_provider: provider, ...formValues };
    // Only include credential fields if they have new values (not empty = keep existing)
    if (!formValues.s3_access_key) delete base.s3_access_key;
    if (!formValues.s3_secret_key) delete base.s3_secret_key;
    if (!formValues.rustfs_api_key) delete base.rustfs_api_key;
    if (!formValues.sharepoint_client_secret) delete base.sharepoint_client_secret;
    return base;
  };

  const handleSave = async () => {
    if (!selectedCompany) return;
    try {
      setIsSaving(true);
      setError('');
      await api.put(`/super-admin/companies/${selectedCompany.id}/storage-config`, buildSavePayload());
      setSuccess('Storage configuration saved successfully.');
      setTimeout(() => setSuccess(''), 4000);
      // Re-fetch to get updated has_* flags
      await fetchStorageConfig(selectedCompany.id);
      fetchCompanies();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || 'Failed to save storage configuration.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedCompany) return;
    try {
      setTestingConnection(true);
      setError('');

      if (testFile && testWithUpload) {
        const formData = new FormData();
        formData.append('file', testFile);
        formData.append('config', JSON.stringify(buildSavePayload()));
        const res = await api.post(
          `/super-admin/companies/${selectedCompany.id}/storage-config/test-upload`,
          formData
        );
        if (res.data.success) {
          setSuccess(`✓ ${res.data.message}${res.data.s3Path ? `\nPath: ${res.data.s3Path}` : ''}`);
          setTestFile(null);
          setTimeout(() => setSuccess(''), 8000);
        } else {
          setError(`✗ ${res.data.message}`);
        }
      } else {
        const res = await api.post(
          `/super-admin/companies/${selectedCompany.id}/storage-config/test-connection`,
          { ...buildSavePayload(), testUpload: false }
        );
        if (res.data.success) {
          setSuccess(`✓ ${res.data.message}`);
          setTimeout(() => setSuccess(''), 5000);
        } else {
          setError(`✗ ${res.data.message}`);
        }
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message || e.message || 'Connection test failed.');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDeleteConfig = async () => {
    if (!selectedCompany) return;
    try {
      setIsSaving(true);
      setError('');
      await api.put(`/super-admin/companies/${selectedCompany.id}/storage-config`, {
        storage_provider: 's3',
        s3_access_key: null, s3_secret_key: null, s3_bucket_name: null,
        s3_region: null, s3_endpoint: null, s3_root_prefix: null,
        rustfs_endpoint: null, rustfs_api_key: null, rustfs_bucket_name: null, rustfs_root_prefix: null,
      });
      setSuccess('Storage configuration cleared.');
      setDeleteConfirm(false);
      await fetchStorageConfig(selectedCompany.id);
      fetchCompanies();
      setTimeout(() => setSuccess(''), 4000);
    } catch {
      setError('Failed to clear storage configuration.');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.company_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading companies…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Page header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm">
            <ServerStackIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 leading-tight">Storage Configuration</h1>
            <p className="text-xs text-slate-500">Manage file storage backends per company</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Global alerts */}
        {error && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 mb-4">
            <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
            <span className="flex-1 whitespace-pre-line">{error}</span>
            <button onClick={() => setError('')} className="shrink-0 text-red-400 hover:text-red-600"><XMarkIcon className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 mb-4">
            <CheckCircleIcon className="w-5 h-5 shrink-0 mt-0.5" />
            <span className="flex-1 whitespace-pre-line">{success}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Company list sidebar ── */}
          <div className="lg:col-span-1 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-700">Companies</h2>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{companies.length}</span>
            </div>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search companies…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
              />
            </div>
            <div className="space-y-2 max-h-[calc(100vh-260px)] overflow-y-auto pr-0.5">
              {filteredCompanies.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No companies found</p>
              ) : filteredCompanies.map(company => {
                const pMeta = company.storage_provider ? PROVIDER_META[company.storage_provider] : null;
                const isSelected = selectedCompany?.id === company.id;
                return (
                  <button
                    key={company.id}
                    onClick={() => handleSelectCompany(company)}
                    className={`w-full text-left bg-white rounded-xl border p-4 transition-all hover:shadow-md ${
                      isSelected ? 'border-blue-400 ring-2 ring-blue-100 shadow-sm' : 'border-slate-200 shadow-sm'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{company.company_name}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{company.company_code}</p>
                      </div>
                      {pMeta ? (
                        <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${pMeta.color}`}>
                          <pMeta.Icon className="w-3 h-3" /> {pMeta.label}
                        </span>
                      ) : (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-400 border border-slate-200">
                          Not set
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Main config panel ── */}
          <div className="lg:col-span-2">
            {!selectedCompany ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center py-28 px-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                  <CloudIcon className="w-8 h-8 text-slate-400" />
                </div>
                <h2 className="text-base font-semibold text-slate-900 mb-1">Select a Company</h2>
                <p className="text-sm text-slate-500 max-w-xs">Choose a company from the list to view and configure its storage backend.</p>
              </div>
            ) : configLoading ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center justify-center py-28">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Loading configuration…</p>
                </div>
              </div>
            ) : config ? (
              <div className="space-y-4">
                {/* Company header + status */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-base font-bold text-slate-900">{selectedCompany.company_name}</h2>
                      <p className="text-xs text-slate-400 mt-0.5">{selectedCompany.company_code}</p>
                    </div>
                    {config.is_configured ? (
                      <div className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <CheckBadgeIcon className="w-4 h-4 text-emerald-600" />
                        <span className="text-xs font-semibold text-emerald-700">
                          {config.storage_provider ? PROVIDER_META[config.storage_provider as StorageProvider]?.label ?? config.storage_provider : ''} Configured
                        </span>
                      </div>
                    ) : (
                      <div className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-xl">
                        <ExclamationTriangleIcon className="w-4 h-4 text-amber-600" />
                        <span className="text-xs font-semibold text-amber-700">Not Configured</span>
                      </div>
                    )}
                  </div>

                  {/* Current config summary */}
                  {config.is_configured && config.storage_provider === 's3' && (
                    <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
                      {[
                        { label: 'Bucket', value: config.s3_bucket_name },
                        { label: 'Region', value: config.s3_region },
                        { label: 'Endpoint', value: config.s3_endpoint },
                        { label: 'Prefix', value: config.s3_root_prefix },
                        { label: 'Access Key', value: config.has_s3_access_key ? '✓ Set' : '✗ Not set' },
                        { label: 'Secret Key', value: config.has_s3_secret_key ? '✓ Set' : '✗ Not set' },
                      ].filter(f => f.value).map(f => (
                        <div key={f.label}>
                          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">{f.label}</p>
                          <p className={`text-sm font-medium mt-0.5 ${f.value?.startsWith('✓') ? 'text-emerald-600' : f.value?.startsWith('✗') ? 'text-red-500' : 'text-slate-700'}`}>
                            {f.value || '—'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {config.is_configured && config.storage_provider === 'rustfs' && (
                    <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
                      {[
                        { label: 'Endpoint', value: config.rustfs_endpoint },
                        { label: 'Bucket', value: config.rustfs_bucket_name },
                        { label: 'Prefix', value: config.rustfs_root_prefix },
                        { label: 'API Key', value: config.has_rustfs_api_key ? '✓ Set' : '✗ Not set' },
                      ].filter(f => f.value).map(f => (
                        <div key={f.label}>
                          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">{f.label}</p>
                          <p className={`text-sm font-medium mt-0.5 ${f.value?.startsWith('✓') ? 'text-emerald-600' : f.value?.startsWith('✗') ? 'text-red-500' : 'text-slate-700'}`}>
                            {f.value || '—'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {config.is_configured && config.storage_provider === 'sharepoint' && (
                    <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Site ID</p>
                        <p className="text-sm font-medium mt-0.5 text-slate-700 font-mono break-all">{config.sharepoint_site_id || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Tenant ID</p>
                        <p className="text-sm font-medium mt-0.5 text-slate-700 font-mono break-all">{config.sharepoint_tenant_id || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Client ID</p>
                        <p className="text-sm font-medium mt-0.5 text-slate-700 font-mono break-all">{config.sharepoint_client_id || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Client Secret</p>
                        {config.has_sharepoint_client_secret
                          ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-semibold text-emerald-700 mt-0.5"><CheckBadgeSolid className="w-3 h-3" /> Set ✓</span>
                          : <p className="text-sm font-medium mt-0.5 text-red-500">Not set</p>}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── View mode: locked read-only display ── */}
                {!isEditing ? (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        {(() => {
                          const meta = PROVIDER_META[provider as StorageProvider];
                          const Icon = meta?.Icon ?? ServerStackIcon;
                          return (
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${meta?.iconBg ?? 'bg-slate-100'}`}>
                              <Icon className={`w-6 h-6 ${meta?.iconColor ?? 'text-slate-500'}`} />
                            </div>
                          );
                        })()}
                        <div>
                          <p className="text-sm font-bold text-slate-900">{PROVIDER_META[provider as StorageProvider]?.label ?? provider}</p>
                          <p className="text-xs text-slate-500">{PROVIDER_META[provider as StorageProvider]?.desc}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors"
                      >
                        Edit Configuration
                      </button>
                    </div>

                    <div className="border border-slate-100 rounded-xl overflow-hidden divide-y divide-slate-100">
                      {provider === 's3' && (<>
                        {([
                          { label: 'Bucket Name', value: formValues.s3_bucket_name },
                          { label: 'Region', value: formValues.s3_region },
                          { label: 'Custom Endpoint', value: formValues.s3_endpoint },
                          { label: 'Folder Prefix', value: formValues.s3_root_prefix },
                        ] as { label: string; value: string | undefined }[]).map(f => (
                          <div key={f.label} className="flex items-center px-4 py-3 gap-4">
                            <span className="w-36 text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">{f.label}</span>
                            <span className="text-sm text-slate-800 font-medium">{f.value || <span className="text-slate-400 italic font-normal">—</span>}</span>
                          </div>
                        ))}
                        <div className="flex items-center px-4 py-3 gap-4">
                          <span className="w-36 text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">Access Key</span>
                          {config?.has_s3_access_key
                            ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-semibold text-emerald-700"><CheckBadgeSolid className="w-3 h-3" /> Configured</span>
                            : <span className="text-xs text-red-500 font-medium">Not set</span>}
                        </div>
                        <div className="flex items-center px-4 py-3 gap-4">
                          <span className="w-36 text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">Secret Key</span>
                          {config?.has_s3_secret_key
                            ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-semibold text-emerald-700"><CheckBadgeSolid className="w-3 h-3" /> Configured</span>
                            : <span className="text-xs text-red-500 font-medium">Not set</span>}
                        </div>
                      </>)}

                      {provider === 'rustfs' && (<>
                        {([
                          { label: 'API Endpoint', value: formValues.rustfs_endpoint },
                          { label: 'Bucket Name', value: formValues.rustfs_bucket_name },
                          { label: 'Folder Prefix', value: formValues.rustfs_root_prefix },
                        ] as { label: string; value: string | undefined }[]).map(f => (
                          <div key={f.label} className="flex items-center px-4 py-3 gap-4">
                            <span className="w-36 text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">{f.label}</span>
                            <span className="text-sm text-slate-800 font-medium">{f.value || <span className="text-slate-400 italic font-normal">—</span>}</span>
                          </div>
                        ))}
                        <div className="flex items-center px-4 py-3 gap-4">
                          <span className="w-36 text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">API Key</span>
                          {config?.has_rustfs_api_key
                            ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-semibold text-emerald-700"><CheckBadgeSolid className="w-3 h-3" /> Configured</span>
                            : <span className="text-xs text-red-500 font-medium">Not set</span>}
                        </div>
                      </>)}

                      {provider === 'sharepoint' && (<>
                        {([
                          { label: 'Site ID', value: formValues.sharepoint_site_id },
                          { label: 'Tenant ID', value: formValues.sharepoint_tenant_id },
                          { label: 'Client ID', value: formValues.sharepoint_client_id },
                        ] as { label: string; value: string | undefined }[]).map(f => (
                          <div key={f.label} className="flex items-center px-4 py-3 gap-4">
                            <span className="w-36 text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">{f.label}</span>
                            <span className="text-sm text-slate-800 font-medium font-mono break-all">{f.value || <span className="text-slate-400 italic font-normal font-sans">—</span>}</span>
                          </div>
                        ))}
                        <div className="flex items-center px-4 py-3 gap-4">
                          <span className="w-36 text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">Client Secret</span>
                          {config?.has_sharepoint_client_secret
                            ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-semibold text-emerald-700"><CheckBadgeSolid className="w-3 h-3" /> Set ✓</span>
                            : <span className="text-xs text-red-500 font-medium">Not set</span>}
                        </div>
                      </>)}
                    </div>

                    {!config?.is_configured && (
                      <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                        <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          <p className="font-semibold mb-0.5">Not configured</p>
                          <p>Photo &amp; document storage won't work until it's configured. Need help? Contact <a href="mailto:support@cruzitservices.com" className="font-semibold underline underline-offset-2 hover:text-amber-900">support@cruzitservices.com</a>.</p>
                        </div>
                      </div>
                    )}

                    <div className="pt-1 flex items-center gap-3">
                      <button
                        onClick={handleTestConnection}
                        disabled={testingConnection}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
                      >
                        {testingConnection
                          ? <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Testing…</>
                          : <><WifiIcon className="w-4 h-4" /> Test Saved Config</>}
                      </button>
                      {config?.is_configured && !deleteConfirm && (
                        <button
                          onClick={() => setDeleteConfirm(true)}
                          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" /> Clear Config
                        </button>
                      )}
                      {deleteConfirm && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                          <ExclamationTriangleIcon className="w-4 h-4 text-red-600 shrink-0" />
                          <span className="text-xs text-red-700 font-medium">Clear all storage credentials?</span>
                          <button onClick={handleDeleteConfig} disabled={isSaving} className="px-3 py-1 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
                            {isSaving ? 'Clearing…' : 'Yes, clear'}
                          </button>
                          <button onClick={() => setDeleteConfirm(false)} className="px-3 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Provider selector */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Storage Provider</h3>
                      <div className="grid grid-cols-3 gap-3">
                        {(Object.entries(PROVIDER_META) as [StorageProvider, typeof PROVIDER_META.s3][]).map(([key, meta]) => {
                          const Icon = meta.Icon;
                          const active = provider === key;
                          return (
                            <button
                              key={key}
                              onClick={() => handleProviderChange(key)}
                              className={`p-4 rounded-xl border text-left transition-all ${
                                active ? meta.ring : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                              }`}
                            >
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${meta.iconBg}`}>
                                <Icon className={`w-5 h-5 ${meta.iconColor}`} />
                              </div>
                              <p className="text-sm font-semibold text-slate-900 mt-2">{meta.label}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{meta.desc}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Config fields */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
                      <h3 className="text-sm font-semibold text-slate-900">
                        {provider === 's3' ? 'S3 Compatible Storage' : provider === 'rustfs' ? 'RustFS Configuration' : 'SharePoint Configuration'}
                      </h3>

                      {provider === 's3' && (<>
                        <CredentialField label="Access Key ID" field="s3_access_key" isConfigured={!!config?.has_s3_access_key} value={formValues.s3_access_key || ''} placeholder="AWS Access Key ID" onChange={handleFieldChange} />
                        <CredentialField label="Secret Access Key" field="s3_secret_key" isConfigured={!!config?.has_s3_secret_key} value={formValues.s3_secret_key || ''} placeholder="AWS Secret Access Key" onChange={handleFieldChange} />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Bucket Name</label>
                            <input type="text" value={formValues.s3_bucket_name || ''} onChange={e => handleFieldChange('s3_bucket_name', e.target.value)} placeholder="my-bucket" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Region</label>
                            <input type="text" value={formValues.s3_region || ''} onChange={e => handleFieldChange('s3_region', e.target.value)} placeholder="us-east-1" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">Custom Endpoint <span className="text-slate-400 font-normal">(optional)</span></label>
                          <input type="text" value={formValues.s3_endpoint || ''} onChange={e => handleFieldChange('s3_endpoint', e.target.value)} placeholder="https://s3.example.com — leave blank for AWS" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">Folder Prefix <span className="text-slate-400 font-normal">(optional)</span></label>
                          <input type="text" value={formValues.s3_root_prefix || ''} onChange={e => handleFieldChange('s3_root_prefix', e.target.value)} placeholder="e.g., media/uploads" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                          <p className="text-xs text-slate-400 mt-1">Optional folder path within the bucket.</p>
                        </div>
                      </>)}

                      {provider === 'rustfs' && (<>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">API Endpoint</label>
                          <input type="text" value={formValues.rustfs_endpoint || ''} onChange={e => handleFieldChange('rustfs_endpoint', e.target.value)} placeholder="https://rustfs.example.com" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                        </div>
                        <CredentialField label="API Key" field="rustfs_api_key" isConfigured={!!config?.has_rustfs_api_key} value={formValues.rustfs_api_key || ''} placeholder="RustFS API key" onChange={handleFieldChange} />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Bucket Name</label>
                            <input type="text" value={formValues.rustfs_bucket_name || ''} onChange={e => handleFieldChange('rustfs_bucket_name', e.target.value)} placeholder="my-bucket" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Folder Prefix <span className="text-slate-400 font-normal">(optional)</span></label>
                            <input type="text" value={formValues.rustfs_root_prefix || ''} onChange={e => handleFieldChange('rustfs_root_prefix', e.target.value)} placeholder="cma/media" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                          </div>
                        </div>
                      </>)}

                      {provider === 'sharepoint' && (<>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">Site ID</label>
                          <input type="text" value={formValues.sharepoint_site_id || ''} onChange={e => handleFieldChange('sharepoint_site_id', e.target.value)} placeholder="e.g. cruzit.sharepoint.com,8f2a…,b71c…" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Tenant ID</label>
                            <input type="text" value={formValues.sharepoint_tenant_id || ''} onChange={e => handleFieldChange('sharepoint_tenant_id', e.target.value)} placeholder="Microsoft Directory (tenant) ID" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Client ID</label>
                            <input type="text" value={formValues.sharepoint_client_id || ''} onChange={e => handleFieldChange('sharepoint_client_id', e.target.value)} placeholder="Azure App (client) ID" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                          </div>
                        </div>
                        <CredentialField
                          label="Client Secret"
                          field="sharepoint_client_secret"
                          isConfigured={!!config?.has_sharepoint_client_secret}
                          value={formValues.sharepoint_client_secret || ''}
                          placeholder="Azure App client secret value"
                          onChange={handleFieldChange}
                          hint="Leave blank to keep the current secret. Only enter a new value to replace it."
                        />
                      </>)}
                    </div>

                    {/* Test connection */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Test Connection</h3>
                      <div className="space-y-3">
                        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input type="checkbox" checked={testWithUpload} onChange={e => setTestWithUpload(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                          Include test file upload
                        </label>
                        {testWithUpload && (
                          <div className="flex items-center gap-2">
                            <label className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-200 transition-colors">
                              <input type="file" accept="image/*,application/pdf,.doc,.docx" onChange={e => setTestFile(e.target.files?.[0] || null)} className="hidden" />
                              <PaperClipIcon className="w-4 h-4" />
                              {testFile ? testFile.name : 'Choose file'}
                            </label>
                            {testFile && <button onClick={() => setTestFile(null)} className="p-1.5 text-slate-400 hover:text-slate-600"><XMarkIcon className="w-4 h-4" /></button>}
                          </div>
                        )}
                        <button onClick={handleTestConnection} disabled={testingConnection || isSaving} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50">
                          {testingConnection ? <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Testing…</> : <><WifiIcon className="w-4 h-4" /> Test Connection</>}
                        </button>
                      </div>
                    </div>

                    {/* Action bar */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          <button onClick={handleSave} disabled={isSaving || testingConnection} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50">
                            {isSaving ? <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Saving…</> : 'Save Configuration'}
                          </button>
                          {config?.is_configured && (
                            <button
                              onClick={() => {
                                setIsEditing(false);
                                setFormValues({
                                  storage_provider: provider,
                                  s3_endpoint: config.s3_endpoint ?? '',
                                  s3_bucket_name: config.s3_bucket_name ?? '',
                                  s3_region: config.s3_region ?? '',
                                  s3_root_prefix: config.s3_root_prefix ?? '',
                                  s3_access_key: '',
                                  s3_secret_key: '',
                                  rustfs_endpoint: config.rustfs_endpoint ?? '',
                                  rustfs_bucket_name: config.rustfs_bucket_name ?? '',
                                  rustfs_root_prefix: config.rustfs_root_prefix ?? '',
                                  rustfs_api_key: '',
                                  sharepoint_client_id: config.sharepoint_client_id ?? '',
                                  sharepoint_client_secret: '',
                                  sharepoint_tenant_id: config.sharepoint_tenant_id ?? '',
                                  sharepoint_site_id: config.sharepoint_site_id ?? '',
                                });
                              }}
                              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                        {config?.is_configured && !deleteConfirm && (
                          <button onClick={() => setDeleteConfirm(true)} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50">
                            <TrashIcon className="w-4 h-4" /> Clear Config
                          </button>
                        )}
                        {deleteConfirm && (
                          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                            <ExclamationTriangleIcon className="w-4 h-4 text-red-600 shrink-0" />
                            <span className="text-xs text-red-700 font-medium">Clear all storage credentials?</span>
                            <button onClick={handleDeleteConfig} disabled={isSaving} className="px-3 py-1 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
                              {isSaving ? 'Clearing…' : 'Yes, clear'}
                            </button>
                            <button onClick={() => setDeleteConfirm(false)} className="px-3 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
